/**
 * UserDirectory — client-side email/id → user-name resolver.
 *
 * Sprint 2026-Feb-10n. Historically the ERP stores the user's *email*
 * into audit fields (`created_by`, `approved_by`, `posted_by`, etc.).
 * This module hydrates a single `{email → name, id → name}` map from
 * `GET /api/users/directory` once at app mount, then serves every UI
 * lookup in O(1) with zero additional round-trips.
 *
 * Contract:
 *   • Fallback: unresolvable input is returned verbatim (matches the
 *     "show email if name unavailable" rule).
 *   • Never throws — if the directory endpoint fails, the app keeps
 *     working (users just see emails as they always did).
 *   • TTL 5 minutes: automatic re-fetch on next `ensure()` call. Also
 *     exposed as `invalidate()` for user-CRUD screens.
 *   • React glue: `useUserDirectory()` hook + `<UserLabel/>` component
 *     live in `components/UserLabel.jsx` — this file is state-only.
 */

import { api } from "./api";

const TTL_MS = 5 * 60 * 1000;

const state = {
  loaded: false,
  loading: null,     // in-flight promise (dedupe concurrent callers)
  stamp: 0,
  byEmail: new Map(),   // email (lowercased) → name
  byId:    new Map(),   // user_id → name
  listeners: new Set(), // fn() → notify subscribers after (re)load
};

function _notify() {
  for (const fn of state.listeners) {
    try { fn(); } catch { /* swallow */ }
  }
}

/** Subscribe to directory refresh events. Returns an unsubscribe fn. */
export function subscribeUserDirectory(fn) {
  state.listeners.add(fn);
  return () => state.listeners.delete(fn);
}

/**
 * Populate the cache. Idempotent + concurrency-safe.
 * Auto-refreshes when the TTL has expired.
 */
export async function ensureUserDirectory() {
  const fresh = state.loaded && (Date.now() - state.stamp) < TTL_MS;
  if (fresh) return;
  if (state.loading) return state.loading;
  state.loading = (async () => {
    try {
      const r = await api.get("/users/directory");
      const rows = Array.isArray(r.data) ? r.data : [];
      const byEmail = new Map();
      const byId = new Map();
      for (const u of rows) {
        const name = (u?.name || "").trim();
        if (!name) continue;
        const em = (u?.email || "").trim().toLowerCase();
        const id = (u?.id || "").trim();
        if (em) byEmail.set(em, name);
        if (id) byId.set(id, name);
      }
      state.byEmail = byEmail;
      state.byId = byId;
      state.loaded = true;
      state.stamp = Date.now();
      _notify();
    } catch {
      // Directory unavailable — keep whatever cache we have. Callers
      // fall back to raw input which is typically the stored email.
    } finally {
      state.loading = null;
    }
  })();
  return state.loading;
}

/** Force the next lookup to re-fetch from the API. Call after
 *  create-user / edit-user / delete-user flows. */
export function invalidateUserDirectory() {
  state.stamp = 0;
}

/**
 * Return the display name for a raw audit value (email OR user_id).
 *
 * Fallback rule (per product spec 2026-Feb):
 *   • name found        → return name
 *   • name missing      → return the original input (typically email)
 *   • empty / null      → return "—"
 */
export function formatUser(value, fallback = "—") {
  if (value === null || value === undefined) return fallback;
  const s = String(value).trim();
  if (!s) return fallback;
  const isEmail = s.includes("@");
  const key = isEmail ? s.toLowerCase() : s;
  const hit = isEmail ? state.byEmail.get(key) : state.byId.get(key);
  return hit || s;
}

/** For PDF-style "Name<br/>small-email" — returns `{name, email}`.
 *  `email` is empty if the input isn't/wasn't an email. */
export function resolveUser(value) {
  if (value === null || value === undefined) return { name: "", email: "" };
  const s = String(value).trim();
  if (!s) return { name: "", email: "" };
  if (s.includes("@")) {
    const key = s.toLowerCase();
    const name = state.byEmail.get(key) || "";
    return { name: name || s, email: name ? key : "" };
  }
  const name = state.byId.get(s) || "";
  return { name: name || s, email: "" };
}

/** Diagnostics — exposed for the debug tool + unit tests. */
export function _debugState() {
  return {
    loaded: state.loaded,
    stamp: state.stamp,
    size: state.byEmail.size + state.byId.size,
  };
}
